import axios, { AxiosResponse } from 'axios';
import config from '../services/config';
import { msalInstance } from './msalInstance';
import { v4 as uuidv4 } from 'uuid';
import { Data, TimesheetPayload, TimesheetResponseDto } from '../interfaces/timesheet';
import toast from 'react-hot-toast';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
// Custom response type for offline-capable responses
export interface OfflineAwareResponse<T = any> extends AxiosResponse<T> {
    offline?: boolean;
}

const api = axios.create({
    baseURL: config.api.baseUrl
});
const apiScopes = [`${config.azureFunctionCLientId}/user_impersonation`];
let reauthRedirectInProgress = false;

const resolveAccount = (): AccountInfo | null => {
    const active = msalInstance.getActiveAccount();
    if (active) return active;

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        return accounts[0];
    }

    return null;
};

const isInteractionRequired = (error: unknown): boolean => {
    if (error instanceof InteractionRequiredAuthError) {
        return true;
    }

    const errorCode = ((error as { errorCode?: string })?.errorCode || '').toLowerCase();
    return ['interaction_required', 'login_required', 'consent_required', 'no_tokens_found', 'user_login_error'].includes(errorCode);
};

api.interceptors.request.use(async (conf) => {
    const checkConnection = (): Promise<boolean> => {
        return new Promise((resolve) => {
            // Try to fetch a small resource to verify internet connection
            fetch('/favicon.ico', {
                method: 'HEAD',
                cache: 'no-store',
                headers: { 'Cache-Control': 'no-cache' }
            })
                .then(response => resolve(response.ok))
                .catch(() => resolve(false));
        });
    };
    // Use a more robust approach to check connection status
    const isOnline = await checkConnection();

    if (!isOnline) {
        // Create a proper error object with additional metadata for the response interceptor
        const offlineError = new Error("You're currently offline");
        // Add properties needed by response interceptor
        Object.assign(offlineError, {
            isOffline: true,
            config: conf,
            isGetRequest: conf.method?.toLowerCase() === 'get',
            message: "You're currently offline. Using cached data from Redux store.",
            response: {
                status: 0,
                statusText: 'offline'
            }
        });

        return Promise.reject(offlineError);
    }

    try {
        // Online path - proceed with normal token acquisition
        const account = resolveAccount();
        if (!account) {
            throw new Error('No active account! Verify a user has been signed in and setActiveAccount has been called.');
        }

        const response = await msalInstance.acquireTokenSilent({
            scopes: apiScopes,
            account: account
        });

        conf.headers.Authorization = `Bearer ${response.accessToken}`;
        return conf;
    } catch (authError: unknown) {
        if (isInteractionRequired(authError) && !reauthRedirectInProgress) {
            reauthRedirectInProgress = true;
            toast.error("Session expirée. Redirection vers la connexion...", { duration: 3000 });

            const account = resolveAccount();
            await msalInstance.acquireTokenRedirect({
                scopes: apiScopes,
                ...(account ? { account } : {})
            });
        }

        console.error("Azure SWA: Authentication error", authError);
        return Promise.reject(authError);
    }
});

// Add response interceptor to handle offline errors
api.interceptors.response.use(
    response => response,
    error => {
        // Check if error is our offline error
        if (error.isOffline) {
            // For GET requests, return a special response with useReduxStore flag
            if (error.isGetRequest) {
                toast.error("You're currently offline, using Cached Data.",{style: {background: '#F57C00', color: "white"}, duration: 3000,icon:  "⚠️",});

                return Promise.resolve({
                    data: null,
                    status: 200,
                    statusText: 'OK (Offline)',
                    headers: {},
                    config: error.config,
                    offline: true,
                    useReduxStore: true // Clear flag to signal using Redux store
                });
            }

            // For POST/creating timesheet
            if (error.config.method.toLowerCase() === 'post' &&
                error.config.url.includes(config.azure.functionApp.saveTimesheetUrl)) {
                // Parse the original timesheet data
                const timesheetData = JSON.parse(error.config.data);

                return Promise.resolve({
                    data: {
                        timesheet: {
                            ...timesheetData,
                            localId: `local_${uuidv4()}`,
                            syncStatus: 'pending'
                        }
                    },
                    status: 200,
                    statusText: 'OK (Offline)',
                    headers: {},
                    config: error.config,
                    offline: true
                });
            }

            // For PUT/update operations
            if (error.config.method.toLowerCase() === 'put' || error.config.method.toLowerCase() === 'patch') {
                const updateData = JSON.parse(error.config.data);

                return Promise.resolve({
                    data: {
                        timesheet: {
                            ...updateData,
                            syncStatus: 'pending',
                            _pendingUpdate: true
                        }
                    },
                    status: 200,
                    statusText: 'OK (Offline)',
                    headers: {},
                    config: error.config,
                    offline: true
                });
            }

            // For DELETE operations
            if (error.config.method.toLowerCase() === 'delete') {
                const urlParts = error.config.url.split('/');
                const resourceId = urlParts[urlParts.length - 1];

                return Promise.resolve({
                    data: {
                        id: resourceId,
                        localId: `delete_${uuidv4()}`
                    },
                    status: 200,
                    statusText: 'OK (Offline)',
                    headers: {},
                    config: error.config,
                    offline: true,
                    _pendingDelete: true
                });
            }

            // Fallback for other operations
            return Promise.resolve({
                data: null,
                status: 200,
                statusText: 'OK (Offline)',
                headers: {},
                config: error.config,
                offline: true
            });
        }

        // Handle other errors (network, server, etc.)
        return Promise.reject(error);
    }
);

// Rest of your API functions remain the same
export const getDataFromGoogleSheet = (): Promise<OfflineAwareResponse<Data>> => api.get(config.azure.functionApp.getDataFromGoogleSheetUrl);

export const getDataFromOneDrive = (): Promise<OfflineAwareResponse<Data>> => api.get(config.azure.functionApp.getDataFromOneDriveUrl);

export const saveTimesheet = async (timesheet: TimesheetPayload): Promise<OfflineAwareResponse<TimesheetResponseDto>> => {
    return api.post(config.azure.functionApp.saveTimesheetUrl, timesheet);
};

export const editTimesheet = async (timesheet: TimesheetPayload): Promise<OfflineAwareResponse<TimesheetResponseDto>> => {
    return api.put(config.azure.functionApp.updateTimesheetUrl + "/" + timesheet.id, timesheet);
};

export const removeTimesheet = (timesheetId: string | number): Promise<OfflineAwareResponse<TimesheetResponseDto>> =>
    api.delete(config.azure.functionApp.deleteTimesheetUrl + "/" + timesheetId);

export const validateByHr = (timesheetId: string) =>
    api.put(config.azure.functionApp.validateByHR + "/" + timesheetId);

export const validateByForeman = (timesheetId: string) =>
    api.put(config.azure.functionApp.validateByForeman + "/" + timesheetId);

export const getTimesheetsByUser = (weekNumber?: number, year?: number) => {
    const params = new URLSearchParams();
    let url = config.azure.functionApp.getTimesheetsByUserUrl;

    if (weekNumber) params.append('weekNumber', weekNumber.toString());

    if (year) params.append('year', year.toString());

    if (params.toString()) {
        url += `?${params.toString()}`;
    }
    return api.get(url)
};

export const getAllTimesheets = (weekNumber?: number, year?: number) => {
    const params = new URLSearchParams();
    let url = config.azure.functionApp.getAllTimesheetsUrl;

    if (weekNumber) params.append('weekNumber', weekNumber.toString());

    if (year) params.append('year', year.toString());

    if (params.toString()) {
        url += `?${params.toString()}`;
    }
    return api.get(url)
};

export const getTimesheetsByForeman = (weekNumber?: number, year?: number) => {
    const params = new URLSearchParams();
    let url = config.azure.functionApp.getForemanTimesheetsUrl;

    if (weekNumber) params.append('weekNumber', weekNumber.toString());

    if (year) params.append('year', year.toString());

    if (params.toString()) {
        url += `?${params.toString()}`;
    }
    return api.get(url)
};

export const getTimesheetsByHR = (weekNumber?: number, year?: number) => {
    const params = new URLSearchParams();
    let url = config.azure.functionApp.getHRTimesheetsUrl;

    if (weekNumber) params.append('weekNumber', weekNumber.toString());

    if (year) params.append('year', year.toString());

    if (params.toString()) {
        url += `?${params.toString()}`;
    }
    return api.get(url)
};

export async function exportTimesheets(payload: { weekNumber: string, year: string }) {
    const params = new URLSearchParams();
    const { weekNumber, year } = payload;
    let url = config.azure.functionApp.exportTimesheetsUrl;
    if (weekNumber) params.append('weekNumber', weekNumber);
    if (year) params.append('year', year);
    if (params.toString()) {
        url += `?${params.toString()}`;
    }

    return api.get(url);
}

export const purgeOldTimesheets = (): Promise<OfflineAwareResponse<any>> => {
  return api.get(config.azure.functionApp.purgeOldTimesheetsUrl);
};
